import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import html2canvas from "html2canvas";
import { authService } from "../../services/api"; // Assuming api.js is in src/services
import { ellipse_bg } from "../../constants/icons";
const EditSignature = () => {
  const navigate = useNavigate();
  const [fullName, setFullName] = useState("");
  const [optionalLine, setOptionalLine] = useState("");
  const [backendSignatureUrl, setBackendSignatureUrl] = useState(null);
  const [stagedUploadedFile, setStagedUploadedFile] = useState(null);
  const [stagedUploadedFilePreview, setStagedUploadedFilePreview] =
    useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingSignature, setIsFetchingSignature] = useState(true);
  const [error, setError] = useState(null);
  const [activeSignatureSource, setActiveSignatureSource] = useState("backend");
  const [isSignatureMarkedForRemoval, setIsSignatureMarkedForRemoval] =
    useState(false);

  const signaturePreviewRef = useRef(null);
  const fileInputRef = useRef(null);
  const qesBlockRef = useRef(null); // Ref for the QES block HTML structure

  useEffect(() => {
    const fetchSignature = async () => {
      setIsFetchingSignature(true);
      setError(null);
      const response = await authService.getSignature();
      if (response.success && response.data.signature_image_s3_url) {
        setBackendSignatureUrl(response.data.signature_image_s3_url);
        setActiveSignatureSource("backend");
      } else {
        setActiveSignatureSource("default");
        if (!response.success && response.error) {
          if (
            !response.error.includes("Failed to fetch signature") ||
            (response.error !== "Request failed with status code 404" &&
              response.error.toLowerCase().indexOf("not found") === -1)
          ) {
            setError(response.error);
          }
        }
      }
      setIsFetchingSignature(false);
    };
    fetchSignature();
  }, []);

  const handleFullNameChange = (e) => {
    setFullName(e.target.value);
    setActiveSignatureSource("text");
    setStagedUploadedFile(null);
    setStagedUploadedFilePreview(null);
    setIsSignatureMarkedForRemoval(false);
  };

  const handleOptionalLineChange = (e) => {
    setOptionalLine(e.target.value);
    setActiveSignatureSource("text");
    setStagedUploadedFile(null);
    setStagedUploadedFilePreview(null);
    setIsSignatureMarkedForRemoval(false);
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setStagedUploadedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setStagedUploadedFilePreview(reader.result);
        setActiveSignatureSource("upload");
        setIsSignatureMarkedForRemoval(false);
      };
      reader.readAsDataURL(file);
      setError(null);
    }
  };

  const handleUploadHandwrittenSignature = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleRemoveHandwrittenSignature = () => {
    setIsSignatureMarkedForRemoval(true);
    setBackendSignatureUrl(null);
    setActiveSignatureSource("default");
    setStagedUploadedFile(null);
    setStagedUploadedFilePreview(null);
    setFullName("");
    setOptionalLine("");
  };

  const handleSaveChanges = async () => {
    setIsLoading(true);
    setError(null);
    let signatureToUpload;

    // If signature is marked for removal, use delete endpoint
    if (isSignatureMarkedForRemoval) {
      const response = await authService.deleteSignature();
      if (response.success) {
        setBackendSignatureUrl(null);
        setStagedUploadedFile(null);
        setStagedUploadedFilePreview(null);
        setActiveSignatureSource("default");
        setIsSignatureMarkedForRemoval(false);
        navigate("/documents");
      } else {
        setError(response.error || "Failed to remove signature.");
      }
      setIsLoading(false);
      return;
    }

    if (activeSignatureSource === "upload" && stagedUploadedFile) {
      const tempContainer = document.createElement("div");
      tempContainer.style.display = "inline-block";
      tempContainer.style.padding = "16px";
      tempContainer.style.backgroundColor = "white";
      tempContainer.style.textAlign = "center";

      const imageWrapper = document.createElement("div");
      imageWrapper.style.width = "400px"; // Define a width for the image area
      imageWrapper.style.height = "150px"; // Define a height for the image area
      imageWrapper.style.display = "flex";
      imageWrapper.style.justifyContent = "center";
      imageWrapper.style.alignItems = "center";
      imageWrapper.style.marginBottom = "16px";

      const img = document.createElement("img");
      img.src = stagedUploadedFilePreview;
      img.style.maxWidth = "100%";
      img.style.maxHeight = "100%";
      img.style.objectFit = "contain";

      imageWrapper.appendChild(img);
      tempContainer.appendChild(imageWrapper);

      if (qesBlockRef.current) {
        const qesClone = qesBlockRef.current.cloneNode(true);
        tempContainer.appendChild(qesClone);
      }

      document.body.appendChild(tempContainer);
      await new Promise((resolve) => setTimeout(resolve, 50)); // Short delay for rendering

      try {
        const canvas = await html2canvas(tempContainer, {
          backgroundColor: "white",
          scale: 2,
          width: tempContainer.offsetWidth,
          height: tempContainer.offsetHeight,
          x: 0,
          y: 0,
        });
        const imageBlob = await new Promise((resolve) =>
          canvas.toBlob(resolve, "image/png")
        );
        signatureToUpload = new File([imageBlob], "signature_with_qes.png", {
          type: "image/png",
        });
      } catch (err) {
        console.error("Error generating combined signature image:", err);
        setError("Could not generate signature image. Please try again.");
        setIsLoading(false);
        document.body.removeChild(tempContainer);
        return;
      } finally {
        if (document.body.contains(tempContainer)) {
          document.body.removeChild(tempContainer);
        }
      }
    } else if (activeSignatureSource === "text" && fullName.trim() !== "") {
      if (signaturePreviewRef.current) {
        try {
          const canvas = await html2canvas(signaturePreviewRef.current, {
            backgroundColor: null,
            scale: 2,
          });
          const imageBlob = await new Promise((resolve) =>
            canvas.toBlob(resolve, "image/png")
          );
          signatureToUpload = new File([imageBlob], "text_signature.png", {
            type: "image/png",
          });
        } catch (err) {
          console.error("Error generating text signature image:", err);
          setError("Could not generate signature image. Please try again.");
          setIsLoading(false);
          return;
        }
      }
    }

    if (signatureToUpload) {
      const formData = new FormData();
      formData.append("signature_image", signatureToUpload);
      const response = await authService.uploadSignature(formData);
      if (response.success && response.data.signature_image_s3_url) {
        setBackendSignatureUrl(response.data.signature_image_s3_url);
        setStagedUploadedFile(null);
        setStagedUploadedFilePreview(null);
        setActiveSignatureSource("backend");
        navigate("/documents");
      } else {
        setError(response.error || "Failed to save signature.");
      }
    } else if (
      activeSignatureSource === "backend" ||
      (activeSignatureSource === "default" &&
        !fullName.trim() &&
        !stagedUploadedFile)
    ) {
      navigate("/documents");
    } else if (!fullName.trim() && !stagedUploadedFile) {
      setError(
        "Please provide a full name for the signature or upload an image."
      );
    }
    setIsLoading(false);
  };

  const handleDeleteSignature = async () => {
    setIsLoading(true);
    setError(null);
    const response = await authService.deleteSignature();
    if (response.success) {
      setBackendSignatureUrl(null);
      setStagedUploadedFile(null);
      setStagedUploadedFilePreview(null);
      setFullName("");
      setOptionalLine("");
      setActiveSignatureSource("default");
      setIsSignatureMarkedForRemoval(false);
    } else {
      setError(response.error || "Failed to delete signature.");
    }
    setIsLoading(false);
  };

  // Common QES Block
  const QESBlock = (
    <div
      ref={qesBlockRef}
      className="mt-4 flex items-center space-x-2 w-full justify-center"
    >
      <div className="text-gray-500">Signed on transferpitch.com</div>
    </div>
  );

  // Check if there's an existing handwritten signature (from backend) or a staged upload
  const hasExistingHandwrittenSignature =
    (backendSignatureUrl && !isSignatureMarkedForRemoval) || stagedUploadedFile;

  let previewContent = null;

  if (activeSignatureSource === "upload" && stagedUploadedFilePreview) {
    previewContent = (
      <div className="text-center">
        <div className="w-[400px] h-[150px] flex items-center justify-center mb-4">
          <img
            src={stagedUploadedFilePreview}
            alt="Uploaded Signature Preview"
            className="max-w-full max-h-full object-contain"
          />
        </div>
        {QESBlock}
      </div>
    );
  } else if (activeSignatureSource === "text" && fullName.trim() !== "") {
    previewContent = (
      <div
        ref={signaturePreviewRef}
        className="bg-white rounded-lg p-6 inline-block text-center border-[#D6DBEA] border"
        style={{
          boxShadow: "0px 2px 10px 0px #171A1F1F",
        }}
      >
        <div className="space-y-1">
          <div
            className="text-3xl font-medium text-gray-900 whitespace-nowrap"
            style={{ fontFamily: "cursive" }}
          >
            {fullName}
          </div>
          {optionalLine && (
            <div
              className="text-lg text-gray-700 whitespace-nowrap"
              style={{ fontFamily: "cursive" }}
            >
              {optionalLine}
            </div>
          )}
        </div>
        {QESBlock}
      </div>
    );
  } else if (activeSignatureSource === "backend" && backendSignatureUrl) {
    previewContent = (
      <div
        className="text-center border-[#D6DBEA] border rounded-lg overflow-hidden"
        style={{
          boxShadow: "0px 2px 10px 0px #171A1F1F",
        }}
      >
        <img
          src={backendSignatureUrl}
          alt="Current Signature"
          className="max-w-full max-h-[200px] object-contain"
        />
      </div>
    );
  } else {
    previewContent = (
      <div
        ref={signaturePreviewRef}
        className="bg-white p-6 inline-block text-center border border-[#D6DBEA] rounded-lg"
        style={{
          boxShadow: "0px 2px 10px 0px #171A1F1F",
        }}
      >
        <div className="space-y-1">
          <div
            className="text-3xl font-medium text-gray-900 whitespace-nowrap"
            style={{ fontFamily: "cursive" }}
          >
            {fullName || "John Doe"}
          </div>
          {optionalLine && (
            <div
              className="text-lg text-gray-700 whitespace-nowrap"
              style={{ fontFamily: "cursive" }}
            >
              {optionalLine}
            </div>
          )}
        </div>
        {QESBlock}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FB]">
      {/* Header */}
      <div className="border-b border-[#D6DBEA] bg-white">
        <div className="flex items-center justify-between px-8 py-4">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 text-gray-400 hover:text-gray-600"
              aria-label="Go back"
              disabled={isFetchingSignature || isLoading}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12.8346 7.00008H1.16797M1.16797 7.00008L7.0013 12.8334M1.16797 7.00008L7.0013 1.16675"
                  stroke="#2C2C2C"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
            <h1 className="text-xl font-bold text-[#202630]">Edit signature</h1>
          </div>
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate("/documents")}
              className="px-4 py-2 bg-white border border-[#E6E6E6] rounded-lg text-[#1E1E1ECC] cursor-pointer disabled:opacity-50"
              disabled={isFetchingSignature || isLoading}
            >
              Cancel
            </button>
            <button
              onClick={handleSaveChanges}
              disabled={isFetchingSignature || isLoading}
              className="px-4 py-2  text-white bg-[#2283FF] rounded-lg hover:bg-[#2283FF] disabled:opacity-50 cursor-pointer"
            >
              {isLoading ? "Saving..." : "Save changes"}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-12 py-8">
        {error && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 border border-red-300 rounded-md">
            {error}
          </div>
        )}

        {/* Loading State */}
        {isFetchingSignature ? (
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#2283FF]"></div>
              <p className="mt-4 text-gray-600">Loading signature...</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-8">
            {/* Left Column - Form */}
            <div className="space-y-6">
              <div>
                <label
                  htmlFor="fullName"
                  className="block mb-2 text-[#000000] text-base font-medium"
                >
                  Full name
                </label>
                <input
                  id="fullName"
                  type="text"
                  value={fullName}
                  onChange={handleFullNameChange}
                  placeholder="Ex. John Doe"
                  className="w-full rounded-lg border border-[#E9E9E9] px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isLoading}
                />
              </div>

              <div>
                <label
                  htmlFor="optionalLine"
                  className="block mb-2 text-[#000000] text-base font-medium"
                >
                  Optional line
                </label>
                <input
                  id="optionalLine"
                  type="text"
                  value={optionalLine}
                  onChange={handleOptionalLineChange}
                  placeholder="Ex. Sporting Director"
                  className="w-full rounded-lg border border-[#E9E9E9] px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isLoading}
                />
                <p className="text-[10px] font-bold text-[#000000] mt-2">
                  Add organisation name, position
                </p>
              </div>

              <div>
                {hasExistingHandwrittenSignature ? (
                  <button
                    onClick={handleRemoveHandwrittenSignature}
                    className="text-red-600 hover:text-red-700 disabled:opacity-50"
                    type="button"
                    disabled={isLoading}
                  >
                    Remove signature
                  </button>
                ) : (
                  <button
                    onClick={handleUploadHandwrittenSignature}
                    className="text-[#2283FF] hover:text-[#1a6acc] disabled:opacity-50"
                    type="button"
                    disabled={isLoading}
                  >
                    Upload handwritten signature
                  </button>
                )}
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  className="hidden"
                  accept="image/*"
                  disabled={isLoading}
                />
              </div>
            </div>

            {/* Right Column - Preview */}
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-4">
                Preview
              </label>
              <div
                className="border border-[#D6DBEA] p-8 min-h-[250px] flex flex-col items-center justify-center bg-white"
                style={{
                  backgroundImage: `url(${ellipse_bg})`,
                  backgroundSize: "cover", // Or 'contain', or a specific value
                  backgroundPosition: "center", // Optional
                  backgroundRepeat: "no-repeat", // Optional
                }}
              >
                {previewContent}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EditSignature;
